import type { CopyId } from '@npc/copy'
import { copyText } from '@npc/platform'
import { Link, useParams, useRouterState } from '@tanstack/react-router'
import { useMemo, useState } from 'react'

import { ApiFailure } from '../../app/errors'
import type {
  ProjectImport,
  ProjectImportClaim,
  ProjectImportLink,
  ProjectImportOneTime,
} from '../../app/project-imports'
import type { RemoteAIInvitationBundle } from '../../app/remote-ai'
import {
  useApproveProjectImport,
  useProjectImport,
  useUnlinkProjectImport,
} from '../../app/queries'
import { Button } from '../../components/button'
import { Card, CardHint, CardTitle } from '../../components/card'
import { Page, PageHeader } from '../../components/page'
import { ScreenError, ScreenLoading } from '../../components/state'
import { Tag, type TagTone } from '../../components/tag'
import { monthDayTime } from '../../app/format'
import { t } from '../../copy'
import { ProjectImportAssigneePicker } from './assignee-picker'

const kindCopy = {
  repository: 'project_import.kind.repository',
  host: 'project_import.kind.host',
  endpoint: 'project_import.kind.endpoint',
  vendor: 'project_import.kind.vendor',
  subscription: 'project_import.kind.subscription',
  unknown: 'project_import.kind.unknown',
  project: 'project_import.kind.project',
  domain: 'project_import.kind.domain',
  deployment: 'project_import.kind.deployment',
  ownership: 'project_import.kind.ownership',
} as const satisfies Record<ProjectImportClaim['kind'], CopyId>

const evidenceTone: Record<ProjectImportClaim['evidence'], TagTone> = {
  ai_declared: 'info',
  platform_verified: 'ok',
  inventory_matched: 'ok',
  missing: 'warn',
  conflict: 'danger',
}

const sourceStatusCopy = {
  setup_failed: 'project_import.source.setup_failed',
  awaiting_assignee: 'project_import.source.awaiting_assignee',
  awaiting_source: 'project_import.source.awaiting',
  importing: 'project_import.source.importing',
  imported: 'project_import.source.imported',
  needs_rebase: 'project_import.source.needs_rebase',
  status_unavailable: 'project_import.source.status_unavailable',
} as const satisfies Record<ProjectImport['source_import']['status'], CopyId>

type SourceStatusCopyId = (typeof sourceStatusCopy)[keyof typeof sourceStatusCopy]

export function sourceStatusCopyOf(status: string): SourceStatusCopyId {
  return (
    (sourceStatusCopy as Readonly<Record<string, SourceStatusCopyId>>)[status] ??
    'project_import.source.status_unavailable'
  )
}

export function canGenerateImportHandoff(status: ProjectImport['source_import']['status']): boolean {
  return status === 'awaiting_assignee' || status === 'awaiting_source'
}

export function ProjectImportDetailScreen() {
  const { importId } = useParams({ strict: false }) as { importId?: string }
  const locationState = useRouterState({
    select: (state) =>
      state.location.state as
        | { oneTime?: ProjectImportOneTime; invitation?: RemoteAIInvitationBundle }
        | undefined,
  })
  if (!importId) return <ScreenLoading shape="detail" width="narrow" />
  return <Loaded importId={importId} oneTime={locationState?.oneTime} invitation={locationState?.invitation} />
}

function Loaded({
  importId,
  oneTime,
  invitation,
}: {
  importId: string
  oneTime?: ProjectImportOneTime
  invitation?: RemoteAIInvitationBundle
}) {
  const query = useProjectImport(importId)
  const [currentInvitation, setCurrentInvitation] = useState(invitation)
  if (query.isPending) return <ScreenLoading shape="detail" width="narrow" />
  if (query.error) return <ScreenError error={query.error} />
  if (!query.data) return <ScreenLoading shape="detail" width="narrow" />
  const item = query.data

  return (
    <Page width="narrow">
      <PageHeader
        title={item.project.name}
        subtitle={item.status_text}
        breadcrumb={<Link to="/projects">{t('nav.project')}</Link>}
      />
      <SourceCard item={item} />
      {canGenerateImportHandoff(item.source_import.status) ? <ProjectImportAssigneePicker importId={item.id} regenerate={Boolean(item.assignee)} onAssigned={async (assigned) => {
        setCurrentInvitation(assigned.invitation)
        await query.refetch()
      }} /> : null}
      <PromptCard
        oneTime={oneTime}
        invitation={currentInvitation}
      />
      <ReviewCard item={item} />
      <LinksCard item={item} />
    </Page>
  )
}

/**
 * 「AI 领过一次地址，源码还没推上来」那一句。只在还在等源码、而地址确实被领走过的时候说：
 * 2026-09-05 一个远程 AI 领到凭证之后在自己的沙箱里弄丢了，用户只从那个 AI 的聊天里知道没成。
 * 导入了 / 正在导入就不说——那时地址被领走是正常的。
 */
export function sourceClaimedHint(item: Pick<ProjectImport, 'source_import'>): string | null {
  const claimed = item.source_import.claimed_at
  if (item.source_import.status !== 'awaiting_source' || claimed === undefined || claimed === null) return null
  return t('project_import.source.claimed_hint', { time: monthDayTime(claimed) })
}

function SourceCard({ item }: { item: ProjectImport }) {
  const retryable =
    item.source_import.status === 'setup_failed' ||
    item.source_import.status === 'needs_rebase' ||
    item.source_import.status === 'awaiting_source' ||
    item.source_import.status === 'awaiting_assignee'

  return (
    <Card className="mb-npc-grid" data-testid="project-import-source">
      <div className="flex flex-wrap items-start justify-between gap-npc-grid-sm">
        <div>
          <CardTitle>{t('project_import.source.title')}</CardTitle>
          <CardHint className="mt-npc-gap-sm">{t(sourceStatusCopyOf(item.source_import.status))}</CardHint>
        </div>
        <Tag tone={item.source_import.status === 'imported' ? 'ok' : retryable ? 'warn' : 'info'}>
          {item.source_import.status}
        </Tag>
      </div>
      {item.managed_repository ? (
        <dl className="mt-npc-grid-sm mb-0 grid gap-npc-gap-xs text-npc-caption">
          <div>
            <dt className="text-npc-ink-soft">{t('project_import.source.repository')}</dt>
            <dd className="m-0 font-mono">{item.managed_repository.full_name}</dd>
          </div>
          {item.source_import.candidate_sha ? (
            <div>
              <dt className="text-npc-ink-soft">{t('project_import.source.candidate')}</dt>
              <dd className="m-0 break-all font-mono">{item.source_import.candidate_sha}</dd>
            </div>
          ) : null}
        </dl>
      ) : null}
      {sourceClaimedHint(item) !== null ? <CardHint className="mt-npc-grid-sm" data-testid="project-import-source-claimed">{sourceClaimedHint(item)}</CardHint> : null}
    </Card>
  )
}

function ReviewCard({ item }: { item: ProjectImport }) {
  const approve = useApproveProjectImport(item.id)
  const [selected, setSelected] = useState<string[]>([])
  const [error, setError] = useState('')
  const canApprove = item.status === 'reviewed'
  const selectableClaims = item.review.claims.filter(claimSelectable)
  const hasSelectable = canApprove && selectableClaims.length > 0
  const canSubmit = hasSelectable && selected.length > 0

  function toggle(key: string, enabled: boolean) {
    if (!enabled) return
    setSelected((current) => (current.includes(key) ? current.filter((item) => item !== key) : [...current, key]))
  }

  async function onApprove() {
    setError('')
    try {
      await approve.mutateAsync(selected)
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause.message : t('error.generic'))
    }
  }

  return (
    <Card className="mt-npc-grid">
      <CardTitle>{t('project_import.review.title')}</CardTitle>
      {item.status === 'awaiting_package' ? (
        <CardHint className="mt-npc-gap-sm">{t('project_import.review.waiting')}</CardHint>
      ) : item.review.claims.length === 0 ? (
        <CardHint className="mt-npc-gap-sm">{t('project_import.review.empty')}</CardHint>
      ) : (
        <>
          {canApprove && hasSelectable ? (
            <CardHint className="mt-npc-gap-sm">{t('project_import.approve.hint')}</CardHint>
          ) : null}
          {canApprove && !hasSelectable ? (
            <CardHint className="mt-npc-gap-sm">{t('project_import.approve.no_selectable')}</CardHint>
          ) : null}
          <ul className="m-0 mt-npc-grid-sm list-none p-0 flex flex-col gap-npc-gap-sm">
            {item.review.claims.map((claim) => {
              const selectable = canApprove && claimSelectable(claim)
              return (
                <ClaimRow
                  key={claim.key}
                  claim={claim}
                  selectable={selectable}
                  checked={selected.includes(claim.key)}
                  onToggle={() => toggle(claim.key, selectable)}
                />
              )
            })}
          </ul>
          {hasSelectable ? (
            <div className="mt-npc-grid-sm flex flex-col gap-npc-gap-sm">
              {!canSubmit ? (
                <CardHint className="m-0">{t('project_import.error.nothing_selected')}</CardHint>
              ) : null}
              {error ? <p className="m-0 text-npc-body-sm text-npc-red-ink">{error}</p> : null}
              <Button
                variant="primary"
                onClick={() => void onApprove()}
                disabled={!canSubmit || approve.isPending}
              >
                {approve.isPending ? t('project_import.approve.submitting') : t('project_import.action.approve')}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </Card>
  )
}

/**
 * 「这次接进来的」那张卡，带逐条撤销。
 *
 * 它与项目页那张卡是**同一份来源账的两个入口**：那边是整批撤（人在项目页上
 * 看着不该在的东西想反悔），这边是逐条撤（人已经在核对页上，正对着清单挑）。
 * 两个入口走的是同一个接口，所以不会出现「一边撤了另一边还显示着」。
 *
 * 撤过的行留着显示、标成「已经撤掉了」，不是从列表里消失——
 * 消失的话，事后想知道「我当时是不是撤过」就只剩去翻审计了。
 */
function LinksCard({ item }: { item: ProjectImport }) {
  const unlink = useUnlinkProjectImport(item.id)
  const [error, setError] = useState('')
  const links = item.links ?? []
  const live = links.filter((link) => !link.released_at)
  if (links.length === 0) return null

  async function release(claimKeys: string[]) {
    setError('')
    try {
      await unlink.mutateAsync({ claim_keys: claimKeys })
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause.message : t('error.generic'))
    }
  }

  return (
    <Card className="mt-npc-grid" data-testid="import-links-card">
      <CardTitle>{t('project_import.links.title')}</CardTitle>
      <CardHint className="mt-npc-gap-sm">{t('project_import.links.pre_existing')}</CardHint>
      <ul className="m-0 mt-npc-grid-sm list-none p-0 flex flex-col gap-npc-gap-sm">
        {links.map((link) => (
          <LinkRow
            key={link.id}
            link={link}
            busy={unlink.isPending}
            onRelease={() => void release([link.claim_key])}
          />
        ))}
      </ul>
      {error ? <p className="m-0 mt-npc-gap-sm text-npc-body-sm text-npc-red-ink">{error}</p> : null}
      {live.length > 0 ? (
        <div className="mt-npc-grid-sm">
          <CardHint className="mb-npc-gap-sm">{t('project_import.unlink.hint')}</CardHint>
          <Button
            variant="ghost"
            onClick={() => void release(live.map((link) => link.claim_key))}
            disabled={unlink.isPending}
            data-testid="import-unlink-all"
          >
            {unlink.isPending ? t('project_import.unlink.submitting') : t('project_import.action.unlink')}
          </Button>
        </div>
      ) : null}
    </Card>
  )
}

function LinkRow({
  link,
  busy,
  onRelease,
}: {
  link: ProjectImportLink
  busy: boolean
  onRelease: () => void
}) {
  const released = Boolean(link.released_at)
  return (
    <li className="rounded-npc-inset border border-npc-border-soft px-npc-card-sm py-npc-card-sm">
      <div className="flex flex-wrap items-center gap-npc-gap-xs">
        <span className="text-npc-body-sm">{link.title}</span>
        <Tag>{t(link.kind === 'host' ? 'project_import.kind.host' : 'project_import.kind.vendor')}</Tag>
        {released ? <Tag tone="neutral">{t('project_import.links.released')}</Tag> : null}
        {!released ? (
          <Button variant="ghost" size="sm" onClick={onRelease} disabled={busy}>
            {t('project_import.action.unlink')}
          </Button>
        ) : null}
      </div>
      {link.created_entity && !released ? (
        <p className="m-0 mt-npc-gap-xs text-npc-caption text-npc-ink-soft">
          {t('project_import.links.created_entity_conditional')}
        </p>
      ) : null}
    </li>
  )
}

function claimSelectable(claim: ProjectImportClaim): boolean {
  return (claim.kind === 'host' || claim.kind === 'vendor') && claim.evidence !== 'conflict'
}

function PromptCard({
  oneTime,
  invitation,
}: {
  oneTime?: ProjectImportOneTime
  invitation?: RemoteAIInvitationBundle
}) {
  const [copied, setCopied] = useState(false)
  const [copyFailed, setCopyFailed] = useState(false)
  const bundle = useMemo(() => {
    if (!oneTime && !invitation) return ''
    const lines: string[] = []
    if (invitation?.prompt) {
      lines.push(invitation.prompt)
    } else if (invitation?.redeem_url) {
      lines.push(`IDENTITY_REDEEM_URL=${invitation.redeem_url}`)
    }
    if (oneTime) {
      const packageURL = typeof window === 'undefined'
        ? oneTime.submit_path
        : new URL(oneTime.submit_path, window.location.origin).toString()
      lines.push(
        oneTime.prompt.text,
        `PACKAGE_SUBMIT_URL=${packageURL}`,
        `PACKAGE_SUBMIT_TOKEN=${oneTime.submit_token}`,
      )
    } else if (!invitation) {
      lines.push(t('project_import.prompt.body'))
    }
    return lines.join('\n')
  }, [oneTime, invitation])

  /**
   * 复制失败要说出来。
   *
   * 最早这里是一句没有 catch 的剪贴板调用：不可用时（非安全上下文、用户拒了
   * 权限、浏览器不支持）promise 直接 reject，`setCopied(true)` 永远不执行，
   * **按钮一动不动、一个字都不说**。
   *
   * 这个静默在别处也许只是难看，在这一屏是会丢东西的：口令**只显示这一次**，
   * 用户点了「复制交代词」、看见按钮没变化，最合理的反应是再点一次，
   * 然后关掉页面——而他手上什么都没有。
   *
   * 所以失败时明说，并指向下面那两行——它们本来就渲染在页面上，手动选中就行。
   *
   * 现在走 `@npc/platform`：那一层不抛，失败是一个返回值。
   * 三档失败（`unsupported` / `denied` / `failed`）这一屏合成同一句话，
   * 因为这里给用户的下一个动作只有一个——手动选中下面那两行。
   * 要分开说得先有三条 `docs/copy/` 模板，那归流 0。
   */
  async function copy() {
    if (!bundle) return
    const r = await copyText(bundle)
    setCopied(r.ok)
    setCopyFailed(!r.ok)
  }

  return (
    <Card tone="green" className="min-w-0 overflow-hidden">
      <CardTitle>{t('project_import.prompt.title')}</CardTitle>
      {bundle ? (
        <>
          <p className="mt-npc-gap-sm mb-0 text-npc-body leading-[1.7]">
            {oneTime ? oneTime.prompt.text : t('project_import.prompt.body')}
          </p>
          <CardHint className="mt-npc-gap-sm">{t('project_import.prompt.once')}</CardHint>
          <pre
            className="mt-npc-grid-sm mb-0 max-w-full overflow-x-auto whitespace-pre-wrap break-all font-mono text-npc-caption"
            data-testid="project-import-prompt"
          >
            {bundle}
          </pre>
          <dl className="mt-npc-grid-sm mb-0 grid min-w-0 gap-npc-gap-xs">
            {oneTime ? (
              <div className="min-w-0">
                <dt className="text-npc-caption text-npc-ink-soft">{t('project_import.prompt.path_label')}</dt>
                {/* 这条路径在同一屏上出现**两次**：这里一次，上面那段交给
                    开发 AI 的提示词里还有一次。用文本去找它会同时命中两个，
                    而「我要的是哪一个」不能靠出现顺序回答——
                    与旁边 import-submit-token 同一个做法，给它一个名字。 */}
                <dd
                  data-testid="import-submit-path"
                  className="m-0 min-w-0 break-all font-mono text-npc-caption"
                >
                  {oneTime.submit_path}
                </dd>
              </div>
            ) : null}
            {oneTime ? (
              <div className="min-w-0">
                <dt className="text-npc-caption text-npc-ink-soft">{t('project_import.prompt.token_label')}</dt>
                <dd className="m-0 min-w-0 break-all font-mono text-npc-caption" data-testid="import-submit-token">
                  {oneTime.submit_token}
                </dd>
              </div>
            ) : null}
          </dl>
          <div className="mt-npc-grid-sm">
            <Button variant="primary" onClick={() => void copy()}>
              {copied ? t('project_import.prompt.copied') : t('project_import.prompt.copy')}
            </Button>
            {copyFailed ? (
              <p className="m-0 mt-npc-gap-sm text-npc-body-sm text-npc-amber-ink">
                {t('project_import.prompt.copy_failed')}
              </p>
            ) : null}
          </div>
        </>
      ) : (
        <CardHint className="mt-npc-gap-sm">{t('project_import.prompt.hidden')}</CardHint>
      )}
    </Card>
  )
}

function ClaimRow({
  claim,
  selectable,
  checked,
  onToggle,
}: {
  claim: ProjectImportClaim
  selectable: boolean
  checked: boolean
  onToggle: () => void
}) {
  const conflict = claim.evidence === 'conflict'
  const enabled = selectable && !conflict
  return (
    <li className="rounded-npc-inset border border-npc-border-soft px-npc-card-sm py-npc-card-sm">
      <div className="flex flex-wrap items-center gap-npc-gap-xs">
        {selectable ? (
          <label className="inline-flex items-center gap-npc-gap-xs">
            <input
              type="checkbox"
              checked={checked}
              disabled={!enabled}
              onChange={onToggle}
              className="size-4 accent-npc-green"
            />
            <span className="text-npc-body-sm">{claim.title}</span>
          </label>
        ) : (
          <span className="text-npc-body-sm">{claim.title}</span>
        )}
        <Tag>{t(kindCopy[claim.kind])}</Tag>
        <Tag tone={evidenceTone[claim.evidence]}>{claim.evidence_text}</Tag>
      </div>
      {claim.idle_claimed ? (
        <p className="m-0 mt-npc-gap-xs text-npc-caption text-npc-ink-soft">{t('project_import.idle.not_reusable')}</p>
      ) : null}
      {claim.notes ? (
        <p className="m-0 mt-npc-gap-xs text-npc-caption text-npc-ink-soft">{claim.notes}</p>
      ) : null}
    </li>
  )
}
